import clsx from 'clsx';
import { useContext } from 'react';
import { useParams } from 'react-router-dom';

import { PreviewContext } from 'pages/ContentViewer/PreviewContext';

import { Button } from 'components/clickable/Button';
import { Link } from 'components/clickable/Link';
import { Collapsible } from 'components/Collapsible';
import { ContentViewerShowResponseContentVersionLessonsInner } from 'generated-api';

import { ReactComponent as Pen } from 'icons/pen.svg';
import { ReactComponent as Book } from 'icons/book.svg';
import { ReactComponent as Camera } from 'icons/camera.svg';
import { ReactComponent as Audio } from 'icons/sound.svg';
import { ReactComponent as Paper } from 'icons/paper.svg';
import { ReactComponent as Checkmark } from 'icons/checkmark.svg';
import { ReactComponent as Cross } from 'icons/cross.svg';
import { ReactComponent as Iframe } from 'icons/iframe.svg';
import { ReactComponent as Lock } from 'icons/lock.svg';

interface StepIconByTypeProps {
  type: string;
}

const StepIconByType = ({ type }: StepIconByTypeProps) => {
  if (type === 'presentation') return <Book />;
  if (type === 'document') return <Paper />;
  if (type === 'video') return <Camera />;
  if (type === 'audio') return <Audio />;
  if (type === 'test') return <Pen />;
  if (type === 'iframe') return <Iframe width={16} height={16} />;
  if (type === 'html') return <Iframe width={16} height={16} />;
  if (type === 'scorm') return <Iframe width={16} height={16} />;
  return <div className="">?</div>;
};

interface SidebarLessonsProps {
  lessons: ContentViewerShowResponseContentVersionLessonsInner[];
  onRegisterParticipation: (stepId: string, courseId: string) => void;
  currentStepId?: number;
  currentLessonId?: number;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  isMarketplacePreview?: boolean;
}

export const SidebarLessons = ({
  lessons,
  onRegisterParticipation,
  currentStepId,
  currentLessonId,
  onClick,
  isMarketplacePreview,
}: SidebarLessonsProps) => {
  const { courseId } = useParams();
  const preview = useContext(PreviewContext);

  return (
    <>
      {lessons?.map((lesson, ix) => {
        const isInCurrentLesson = lesson.id === currentLessonId;
        const numberOfFinishedSteps = lesson.steps?.filter(
          (step) => step.step_participation?.state === 'completed'
        ).length;
        const isLessonDone = numberOfFinishedSteps === lesson.steps?.length;
        const isLastLesson = ix + 1 === lessons.length;
        // marketplace preview gives access to the first lesson only (lesson_policy.rb)
        const isLocked = isMarketplacePreview && lesson.position !== 1;

        const lessonHeadingContent = (
          <div className="tw-flex">
            <div className="tw-flex tw-w-48 tw-shrink-0 tw-items-center tw-justify-center tw-text-white">
              {isLocked && <Lock width={18} height={18} />}
              {!isLocked && isLessonDone && <Checkmark width={14} strokeWidth={2} />}
            </div>
            <div className="">
              <h4
                className={clsx(
                  'tw-text-14',
                  !isLocked && isLessonDone ? 'tw-text-lightGreen' : 'tw-text-white'
                )}
              >
                {lesson.position}. {lesson.name}
              </h4>
              {numberOfFinishedSteps !== undefined && (
                <div className="tw-text-12 tw-text-lightGray">
                  {numberOfFinishedSteps}/{lesson.steps?.length}
                </div>
              )}
            </div>
          </div>
        );

        return (
          <Collapsible
            key={lesson.id}
            defaultCollapsed={isLocked}
            toggleOnHeadingClick={isLocked}
            className={clsx(
              'tw-border-t tw-border-white/20 tw-pt-10',
              isLastLesson ? 'tw-pb-0' : 'tw-pb-10',
              isInCurrentLesson && 'tw-bg-white/5',
              isLocked && 'tw-opacity-45'
            )}
            headingClassName={clsx(!currentStepId && isInCurrentLesson && 'tw-bg-white/15')}
            chevronColor={isLessonDone ? '#88C425' : undefined}
            heading={
              isLocked ? (
                lessonHeadingContent
              ) : (
                <Link
                    kind="bare"
                    to={`/learn/${courseId}/lesson/${lesson.id}`}
                    scrollIntoView={!currentStepId && currentLessonId === lesson.id}
                    onClick={onClick}
                    disableFocus
                >
                {lessonHeadingContent}
                </Link>
              )
            }
          >
            <div className="tw-text-white">
              {lesson.steps?.map((step) => {
                const isCompleted = step.step_participation?.state === 'completed';
                const isFailed = step.step_participation?.repeated_study;
                if (isLocked) {
                  // clicking locked content is what surfaces the "preview ends here" modal
                  return (
                    <div
                      key={step.id}
                      role="button"
                      tabIndex={0}
                      onClick={preview?.showPreviewEndModal}
                      className="tw-flex tw-h-[45px] tw-cursor-pointer tw-items-center tw-gap-12 tw-pl-16 hover:tw-bg-white/15"
                    >
                      <div className="tw-flex tw-w-16 tw-shrink-0 tw-justify-center">
                        <Lock width={14} height={14} />
                      </div>
                      <StepIconByType type={step.element_types[0]} />
                      <span className="tw-text-14">
                        {lesson.position}.{step.position} {step.name}
                      </span>
                      {!!step.study_materials?.length && (
                        <Paper className="tw-ml-auto tw-mr-20 tw-flex-shrink-0" />
                      )}
                    </div>
                  );
                }
                return (
                  <Link
                    kind="bare"
                    key={step.id}
                    to={`/learn/${courseId}/lesson/${lesson.id}/step/${step.id}`}
                    className={clsx(
                      'tw-flex tw-h-[45px] tw-cursor-pointer tw-items-center tw-gap-12 tw-pl-16 hover:tw-bg-white/15',
                      step.id === currentStepId && 'tw-bg-white/15'
                    )}
                    scrollIntoView={step.id === currentStepId}
                    onClick={onClick}
                    disableFocus
                  >
                    {courseId && step.element_types[0] !== 'test' && (
                      <Button
                        kind="bare"
                        onClick={(e) => {
                          e?.preventDefault();
                          onRegisterParticipation(step.id.toString(), courseId);
                        }}
                        disabled={isFailed}
                        className="tw-relative tw-flex tw-h-16 tw-w-16 tw-shrink-0 tw-items-center tw-justify-center tw-rounded tw-bg-black/40"
                      >
                        {isCompleted && <Checkmark className="tw-absolute" width={10} />}
                        {isFailed && !isCompleted && <Cross width={10} />}
                      </Button>
                    )}

                    {step.element_types[0] === 'test' && (
                      <div className="tw-flex tw-w-16 tw-shrink-0 tw-justify-center">
                        {isCompleted && <Checkmark />}
                      </div>
                    )}
                    <StepIconByType type={step.element_types[0]} />
                    <span className={clsx(
                        'tw-text-14',
                        isCompleted && 'tw-text-lightGreen',
                        isFailed && !isCompleted && 'tw-text-red'
                    )}>
                      {lesson.position}.{step.position} {step.name}
                    </span>
                    {!!step.study_materials?.length && (
                      <Paper className="tw-ml-auto tw-mr-20 tw-flex-shrink-0" />
                    )}
                  </Link>
                );
              })}
            </div>
          </Collapsible>
        );
      })}
    </>
  );
};
